import React, { useState, ChangeEvent, useRef, useEffect, useCallback } from 'react';
import styles from './commissionModalStyles.module.css';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { ApiResponse, Commission, EmployeeSuggestion } from '../../Models/ProCoverageApiResponse';
import AuthManager from '../../Managers/Auth.manager';
import { useDispatch } from 'react-redux';
import allActions from '../../Store/Actions/AllActions';
import IdentityManager from '../../Managers/Identity.manager';
import Constants from '../../Common/Constants';

interface CommissionModalProps {
  commissions: Commission[];
  onClose: (updatedCommissions: Commission[]) => void;
  orderNo: string;
  enterpriseCode: string;
  customerId: string;
  primeLineNumber?: string;
}

const CommissionModal: React.FC<CommissionModalProps> = ({
  commissions,
  onClose,
  orderNo,
  enterpriseCode,
  customerId,
  primeLineNumber,
}) => {
  const [currentCommissions, setCurrentCommissions] = useState<Commission[]>(commissions);
  const [newCommissionData, setNewCommissionData] = useState<Omit<Commission, 'id'>>({
    employeeNumber: '',
    employeeName: '',
    percentage: '',
    commissionType: "Sales Commission",
  });
  const [showAddForm, setShowAddForm] = useState<boolean>(true);
  const [editingField, setEditingField] = useState<{ id: string; field: string } | null>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [suggestions, setSuggestions] = useState<EmployeeSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const initialCommissionsRef = useRef<Commission[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsModalOpen(true);

    const initializeCommissions = async () => {
      if (commissions.length === 0) {
        dispatch(allActions.actionLoaderActions.showLoader());
        try {
          const empId = IdentityManager.getUserProperty(Constants.EmployeeID);
          if (!empId) {
            toast.warn('Could not retrieve current user to set default commission.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
            initialCommissionsRef.current = [];
            setCurrentCommissions([]);
            return;
          }

          const apiResponse: ApiResponse = await AuthManager.GetSuggestionsFromOkta(empId);
          let userName = 'Current User'; // Fallback name
          let formattedEmpId = String(empId).padStart(6, '0');

          if (apiResponse.success && apiResponse.data?.data?.length > 0) {
            const userDetails = apiResponse.data.data[0];
            userName = userDetails.displayName;
            formattedEmpId = String(userDetails.employeeId).padStart(6, '0');
          } else {
            toast.warn('Could not verify user details, using defaults.',{ 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
          }

          const defaultCommission: Commission = {
            id: `default-${Date.now()}`,
            employeeNumber: formattedEmpId,
            employeeName: userName,
            percentage: '100',
            commissionType: 'Sales Commission',
          };

          const newCommissions = [defaultCommission];
          setCurrentCommissions(newCommissions);
          initialCommissionsRef.current = JSON.parse(JSON.stringify(newCommissions));

        } catch (error) {
          console.error('Failed to set default commission:', error);
          toast.error('An error occurred while setting default user commission.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
          initialCommissionsRef.current = [];
          setCurrentCommissions([]);
        } finally {
            dispatch(allActions.actionLoaderActions.hideLoader());
        }

      } else {
        initialCommissionsRef.current = JSON.parse(JSON.stringify(commissions));
      }
      setHasUnsavedChanges(false);
    };

    initializeCommissions();

  }, []);

  useEffect(() => {
    const currentCommsSimplified = currentCommissions.map(c => ({
      employeeNumber: c.employeeNumber,
      employeeName: c.employeeName,
      percentage: c.percentage,
      commissionType: c.commissionType,
    })).sort((a, b) => (a.employeeNumber || "").localeCompare(b.employeeNumber || ""));
    const initialCommsSimplified = initialCommissionsRef.current.map(c => ({
      employeeNumber: c.employeeNumber,
      employeeName: c.employeeName,
      percentage: c.percentage,
      commissionType: c.commissionType,
    })).sort((a, b) => (a.employeeNumber || "").localeCompare(b.employeeNumber || ""));

    setHasUnsavedChanges(JSON.stringify(currentCommsSimplified) !== JSON.stringify(initialCommsSimplified));
  }, [currentCommissions]);


  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 14px',
    },
  };

  useEffect(() => {
    if (editingField && textFieldRef.current) {
      textFieldRef.current.focus();
      textFieldRef.current.select();
    }
  }, [editingField]);


  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const fetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.length < 3) {
        setSuggestions([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
      const apiResponse: ApiResponse = await AuthManager.GetSuggestionsFromOkta(query);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data.data)) {
        const filteredAndMappedSuggestions: EmployeeSuggestion[] = apiResponse.data.data
          .filter(item => item.employeeId && item.employeeId !== '0') // Filter out null, undefined, empty string, and '0'
          .map(item => {
            const formattedEmployeeId = item.employeeId.padStart(6, '0');

            return {
              employeeId: formattedEmployeeId,
              displayName: item.displayName,
              email: item.email,
            };
          });

        setSuggestions(filteredAndMappedSuggestions);
        if (filteredAndMappedSuggestions.length === 0) {
          toast.warning('No user found with that search term.',{ 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
        }
      } else {
        setSuggestions([]);
        toast.error(apiResponse.msg || 'No valid data in suggestions response.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      toast.error('Failed to fetch suggestions. Please check console for details.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
    } finally {
      setSearchLoading(false);
    }
    }, 1000),
    []
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value) {
      setNewCommissionData(prev => ({
        ...prev,
        employeeNumber: '',
        employeeName: '',
      }));
    }
    if (value) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setSearchLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: EmployeeSuggestion) => {
    setNewCommissionData(prev => ({
      ...prev,
      employeeNumber: suggestion.employeeId,
      employeeName: suggestion.displayName,
    }));
    setSearchTerm(suggestion.displayName);
    setSuggestions([]);
  };

  const handleNewCommissionInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'percentage') {
      const regex = /^(100|[1-9][0-9]?)$/;
      // Allow empty string OR a valid number between 1-100
      if (value === '' || regex.test(value)) {
        setNewCommissionData(prev => ({
          ...prev,
          [name]: value,
        }));
      } else {
        toast.error('Percentage must be a whole number between 1 and 100.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
      }
    } else {
      setNewCommissionData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const calculateTotalPercentage = (commissionsArr: Commission[]): number => {
    return commissionsArr.reduce((sum, comm) => sum + parseFloat(comm.percentage || '0'), 0);
  };

  const handleUpdateAllCommissions = async () => {
    setIsSaving(true);
    dispatch(allActions.actionLoaderActions.showLoader());

    try {
      let commissionsToApply = [...currentCommissions];

      if (newCommissionData.employeeName && newCommissionData.percentage) {
        const percentageAsNumber = parseFloat(newCommissionData.percentage);
        if (isNaN(percentageAsNumber) || percentageAsNumber <= 0 || percentageAsNumber > 100) {
          toast.error('Percentage for new commission must be a number between 0 and 100.', { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            })
          setIsSaving(false);
          dispatch(allActions.actionLoaderActions.hideLoader());
          return;
        }

        const existingIndex = commissionsToApply.findIndex(
          (c) => c.employeeNumber === newCommissionData.employeeNumber
        );

        const newCommWithId: Commission = {
          ...newCommissionData,
          id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        };

        let tempCommissions = [...commissionsToApply];
        if (existingIndex > -1) {
          tempCommissions[existingIndex] = {
            ...tempCommissions[existingIndex],
            ...newCommissionData,
          };
        } else {
          tempCommissions.push(newCommWithId);
        }

        const totalPercentageAfterAdd = calculateTotalPercentage(tempCommissions);
        if (totalPercentageAfterAdd > 100) {
          toast.error(`Adding this commission would make the total percentage exceed 100% (${totalPercentageAfterAdd.toFixed(2)}%). Please adjust.`, { 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
          setIsSaving(false);
          dispatch(allActions.actionLoaderActions.hideLoader());
          return;
        }
        commissionsToApply = tempCommissions;
      }

      setCurrentCommissions(commissionsToApply);
      initialCommissionsRef.current = JSON.parse(JSON.stringify(commissionsToApply));
      setHasUnsavedChanges(false);
      setNewCommissionData({ employeeNumber: '', employeeName: '', percentage: '', commissionType: 'Sales Commission'});
      setSearchTerm('');
      setShowAddForm(false);
    } catch (error) {
      console.error('Error processing commissions locally:', error);
      toast.error('Failed to process commissions locally. Please try again.',{ 
              position: "top-center",  autoClose: 5000 ,
              style: { maxWidth: '100vw', wordBreak: 'break-word' }
            });
    } finally {
      setIsSaving(false);
      dispatch(allActions.actionLoaderActions.hideLoader());
    }
  };

  const handleRemoveCommissionLocally = (id: string) => {
    const updatedCommissions = currentCommissions.filter(comm => comm.id !== id);
    setCurrentCommissions(updatedCommissions);
  };

  const handleInlineChange = (e: ChangeEvent<HTMLInputElement>, id: string, field: keyof Omit<Commission, 'id'>) => {
    const { value } = e.target;
    setCurrentCommissions(prevComms =>
      prevComms.map(c => {
        if (c.id === id) {
          if (field === 'percentage') {
            const regex = /^(100|[1-9][0-9]?)$/;
            // Allow empty string OR a valid number
            if (value === '' || regex.test(value)) {
              const tempComms = prevComms.map(tempC =>
                tempC.id === id ? { ...tempC, [field]: value } : tempC
              );
              const totalPercentage = calculateTotalPercentage(tempComms);

              if (totalPercentage > 100) {
                toast.error(`Total commission percentage cannot exceed 100% (${totalPercentage.toFixed(2)}%). Please adjust.`, { 
                position: "top-center",  autoClose: 5000 ,
                  style: { maxWidth: '100vw', wordBreak: 'break-word' }
                });
                return c; // Revert
              }
              return { ...c, [field]: value }; // Apply valid change
            }
            return c; // Revert on invalid character
          }
          return { ...c, [field]: value };
        }
        return c;
      })
    );
  };

  const handleBlur = (id: string, field: keyof Omit<Commission, 'id'>) => {
    const commissionToBlur = currentCommissions.find(c => c.id === id);

    // If the field was a percentage and was left empty, set it to '1'.
    if (commissionToBlur && field === 'percentage' && commissionToBlur.percentage === '') {
      setCurrentCommissions(prevComms =>
        prevComms.map(c =>
          c.id === id ? { ...c, percentage: '1' } : c
        )
      );
    }
    setEditingField(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, id: string, field: keyof Omit<Commission, 'id'>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      setEditingField(null);
    }
  };

  const handleClickToEdit = (id: string, field: keyof Omit<Commission, 'id'>) => {
    if (!isSaving) {
      setEditingField({ id, field: field as string });
    }
  };

  const renderField = (comm: Commission, field: keyof Omit<Commission, 'id'>) => {
    const isEditing = editingField?.id === comm.id && editingField?.field === field;
    const value = comm[field];
    const isEditableField = field === 'percentage';

    if (field === 'employeeNumber' || field === 'employeeName' || field === 'commissionType') {
      return <span>{value}</span>;
    }
    return (
      <>
        {isEditing ? (
          <TextField
            inputRef={textFieldRef}
            type={field === 'percentage' ? 'number' : 'text'}
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleInlineChange(e, comm.id, field)}
            onBlur={() => handleBlur(comm.id, field)}
            onKeyDown={(e) => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, comm.id, field)}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              min: field === 'percentage' ? 1 : undefined,
              max: field === 'percentage' ? 100 : undefined,
              step: field === 'percentage' ? 1 : undefined,
            }}
            sx={{
              ...textFieldSx,
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '0.9em',
                width: "80px"
              },
            }}
            disabled={isSaving}
          />
        ) : (
            <span 
            className={`${styles.inlineDisplayText} ${isEditableField ? styles.editableField : ''}`}
            onClick={() => handleClickToEdit(comm.id, field)}
            style={{ width: '50px', display: 'inline-block' }}
            >
            {value}{field === 'percentage' ? '%' : ''}
            </span>
        )}
      </>
    );
  };

  const isAddFormValid = () => {
    const newPercentage = parseFloat(newCommissionData.percentage || '0');
    return (
      newCommissionData.employeeName &&
      newCommissionData.percentage &&
      newPercentage > 0 &&
      newPercentage <= 100
    );
  };

  const totalCurrentPercentage = calculateTotalPercentage(currentCommissions);
  const totalIsInvalid = currentCommissions.length > 0 && totalCurrentPercentage !== 100;

  const handleCloseModal = () => {
    const finalTotalPercentage = calculateTotalPercentage(currentCommissions);

    if (finalTotalPercentage !== 100) {
      toast.error(`Total commission must be exactly 100%. Current total is ${finalTotalPercentage.toFixed(2)}%.`, {
        position: "top-center", autoClose: 5000,
        style: { maxWidth: '100vw', wordBreak: 'break-word' }
      });
      return; 
    }

    const hasEmptyFields = currentCommissions.some(c => !c.percentage || !c.commissionType);
    if (hasEmptyFields) {
        toast.error('All commission fields (Percentage, Type) must be filled before saving.', {
            position: "top-center", autoClose: 5000,
            style: { maxWidth: '100vw', wordBreak: 'break-word' }
        });
        return; 
    }

    setIsModalOpen(false);
    setTimeout(() => {
      onClose(currentCommissions);
    }, 200);
  };

  return (
    <div className={`${styles.modalOverlay} ${isModalOpen ? styles.isOpen : ''}`}>
      <div className={styles.commissionModalContainer}>
        <div className={styles.modalHeader}>
          <h2>Manage Commissions</h2>
          <button className={styles.modalCloseButton} onClick={handleCloseModal} disabled={isSaving}>
            ×
          </button>
        </div>
        <div className={styles.modalContent}>
         <div className={styles.noteText}>
          <div>
              Current Total Commission: <span style={{ fontWeight: 'bold', color: totalIsInvalid ? 'red' : 'inherit' }}>{totalCurrentPercentage.toFixed(2)}%</span>.
              Commissions <b>must</b> total exactly 100%.
              {totalIsInvalid && (
                  <span style={{ marginLeft: '5px' }}>
                      Please adjust percentages. The current total is {totalCurrentPercentage > 100 ? 'over' : 'under'} 100%.
                  </span>
              )}
          </div>
      </div>
          <div className={styles.commissionListModal}>
            <div className={styles.commissionHeader}>
              <div className={styles.headerItem}>Employee #</div>
              <div className={styles.headerItem}>Employee Name</div>
              <div className={styles.headerItem}>Percentage</div>
              <div className={styles.headerItem}>Commission Type</div>
              <div className={`${styles.headerItem} ${styles.actionHeader}`}>Actions</div>
            </div>
            {currentCommissions.length > 0 ? (
              currentCommissions.map((comm) => (
                <div key={comm.id} className={styles.commissionRow}>
                  <div className={`${styles.rowItem} ${editingField?.id === comm.id && editingField?.field === 'employeeNumber' ? styles.editing : ''}`}>
                    {renderField(comm, 'employeeNumber')}
                  </div>
                  <div className={`${styles.rowItem} ${editingField?.id === comm.id && editingField?.field === 'employeeName' ? styles.editing : ''}`}>
                    {renderField(comm, 'employeeName')}
                  </div>
                  <div className={`${styles.rowItem} ${editingField?.id === comm.id && editingField?.field === 'percentage' ? styles.editing : ''}`}>
                    {renderField(comm, 'percentage')}
                  </div>
                  <div className={`${styles.rowItem} ${editingField?.id === comm.id && editingField?.field === 'commissionType' ? styles.editing : ''}`}>
                    {renderField(comm, 'commissionType')}
                  </div>
                  <div className={`${styles.rowItem} ${styles.actionItem}`}>
                    <Tooltip title="Remove Commission">
                      <button
                        className={styles.removeCommissionButton}
                        onClick={() => handleRemoveCommissionLocally(comm.id)}
                        aria-label="Remove commission"
                        disabled={isSaving}
                      >
                        <DeleteIcon sx={{ fontSize: '1.2em' }} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.noCommission}>
                No commission details found.
              </div>
            )}
          </div>
          <div
            className={styles.addCommissionToggle}
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingField(null);
              setSearchTerm('');
              setSuggestions([]);
              setNewCommissionData({ employeeNumber: '', employeeName: '', percentage: '', commissionType: 'Sales Commission' });
            }}
          >
            <span style={{ marginRight: '8px' }}>{showAddForm ? '−' : '+'}</span>
            {showAddForm ? 'Hide Add Commission Form' : 'Add New Commission'}
          </div>
          {showAddForm && (
            <div className={styles.addCommissionForm}>
              <div className={styles.formField}>
                <div className={styles.searchInputContainer}>
                  <TextField
                    inputRef={searchInputRef}
                    id="employeeSearch"
                    name="employeeSearch"
                    placeholder="Search By Email or Employee ID"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    disabled={searchLoading || isSaving}
                    onFocus={() => {
                      if (newCommissionData.employeeName && searchTerm === newCommissionData.employeeName) {
                        setSearchTerm('');
                        setNewCommissionData(prev => ({ ...prev, employeeName: '', employeeNumber: '' }));
                      }
                    }}
                    InputProps={{
                      endAdornment: searchLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={textFieldSx}
                  />
                  {suggestions.length > 0 && searchTerm.length >= 3 && (
                    <ul className={styles.suggestionsList}>
                      {suggestions.map((suggestion) => (
                        <li key={suggestion.employeeId} onClick={() => handleSelectSuggestion(suggestion)}>
                          <span className={styles.suggestionDisplayName}>{suggestion.displayName}</span>
                          <span className={styles.suggestionDetails}>({suggestion.employeeId}) {suggestion.email && ` - ${suggestion.email}`}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {searchTerm.length >= 3 && !searchLoading && suggestions.length === 0 && !newCommissionData.employeeName && (
                    <div className={styles.noUserFound}>No user found.</div>
                  )}
                </div>
              </div>
              <div className={styles.formField}>
                <TextField
                  id="employeeNumber"
                  name="employeeNumber"
                  placeholder="Employee #"
                  variant="outlined"
                  fullWidth
                  value={newCommissionData.employeeNumber}
                  onChange={handleNewCommissionInputChange}
                  disabled={!!newCommissionData.employeeName || isSaving}
                  sx={textFieldSx}
                />
              </div>
              <div className={styles.formField}>
                <TextField
                  type="text"
                  id="percentage"
                  name="percentage"
                  placeholder="Percentage (1-100)"
                  variant="outlined"
                  fullWidth
                  value={newCommissionData.percentage}
                  onChange={handleNewCommissionInputChange}
                  onBlur={() => {
                      if (newCommissionData.percentage === '') {
                          setNewCommissionData(prev => ({ ...prev, percentage: '1' }));
                      }
                  }}
                  disabled={isSaving}
                  sx={textFieldSx}
                />
              </div>
              <div className={styles.formField}>
                <TextField
                  id="commissionType"
                  name="commissionType"
                  label="Commission Type"
                  variant="outlined"
                  fullWidth
                  value={newCommissionData.commissionType}
                  disabled={true} 
                  sx={textFieldSx}
                />
              </div>
              <button
                className={styles.saveCommissionButton}
                onClick={handleUpdateAllCommissions}
                disabled={isSaving || !isAddFormValid()}
              >
                {isSaving ? 'Processing...' : 'Apply Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionModal;